/**
 * Renders every unread broadcast (from `/api/broadcasts/unread`) as a
 * dismissible banner across the top of the portal. Hitting X marks the
 * broadcast read for the current user (POST /broadcasts/:id/read).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { X, Info, Tag, AlertTriangle } from "lucide-react";

const KIND_META = {
  info:   { icon: Info,          className: "border-sky-700 bg-sky-950/50 text-sky-100" },
  sale:   { icon: Tag,           className: "border-amber-700 bg-amber-950/50 text-amber-100" },
  urgent: { icon: AlertTriangle, className: "border-rose-700 bg-rose-950/50 text-rose-100" },
};

export default function BroadcastBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/broadcasts/unread");
        if (alive) setItems(r.data.unread || []);
      } catch {}
    };
    load();
    // Poll every 60s so a fresh admin blast shows up without a refresh.
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const dismiss = async (id) => {
    setItems((p) => p.filter((b) => b.id !== id));
    try { await api.post(`/broadcasts/${id}/read`); } catch {}
  };

  if (!user || items.length === 0) return null;
  return (
    <div className="sticky top-0 z-40 space-y-1" data-testid="broadcast-banner">
      {items.map((b) => {
        const meta = KIND_META[b.kind] || KIND_META.info;
        const Icon = meta.icon;
        return (
          <div key={b.id} className={`border-b px-4 py-2.5 flex items-start gap-3 ${meta.className}`} data-testid={`broadcast-banner-${b.id}`}>
            <Icon size={15} className="mt-0.5 shrink-0" />
            <div className="flex-1 text-sm break-words">
              {b.message}
              <span className="text-[10px] uppercase tracking-widest ml-2 opacity-60 font-mono-tc">
                — {b.sent_by_name}
              </span>
            </div>
            <button
              onClick={() => dismiss(b.id)}
              className="opacity-70 hover:opacity-100 shrink-0"
              title="Dismiss"
              data-testid={`broadcast-dismiss-${b.id}`}
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
